import { Link, useRouteMatch } from 'react-router-dom'
import { batch, useDispatch, useSelector } from 'react-redux'
import { useAlert } from 'features/dialog/Alert'
import { useCallback, useEffect, useMemo, useState } from 'react'
import Table from 'components/table/Table'
import SelectBox from 'components/form/SelectBox'
import PropTypes from 'prop-types'
import api from 'api/axios/foka'
import { CHANNEL_ADD_REFERRER } from 'features/channel/add/hooks/useChannelAddReferrer'
import Loading from 'components/loading/Loading'
import Limitations from 'features/Limitations'
import { useConfirm } from '../../dialog/Confirm'
import RadioButton from '../../../components/form/RadioButton'
import { BizChannelTypeNames, BizChannelTypes } from '../../Codes'
import { selectChannel, setBizChannelType } from './creativeAddSlice'
import { reset as resetImport } from './import/creativeImportSlice'
import {
  inactiveAllAsset,
  resetActiveAssets,
  setChannel,
} from './build/buildCreativeSlice'

const from = CHANNEL_ADD_REFERRER.CREATIVE
const maxCount = Limitations.BIZ_CHANNEL_MAX_CREATIVES

const SelectChannel = ({ readOnly }) => {
  const dispatch = useDispatch()
  const { params } = useRouteMatch()
  const {
    channels,
    selectedChannel,
    channelsError,
    isChannelsLoading,
    bizChannelType,
  } = useSelector(state => state.creative.creativeAdd)
  const [Confirm, showConfirm] = useConfirm()
  const [CreativeLimitAlert, showCreativeLimitAlert] = useAlert()
  const [nextBizChannelType, setNextBizChannelType] = useState(bizChannelType)
  const filteredChannels = useMemo(
    () =>
      channels.filter(
        bizChannel => bizChannel.bizChannelType === bizChannelType
      ),
    [bizChannelType, channels]
  )
  const handleChange = type => {
    setNextBizChannelType(type)
    showConfirm()
  }
  const handleClose = ({ isConfirmed }) => {
    if (isConfirmed) {
      batch(() => {
        dispatch(setBizChannelType(nextBizChannelType))
        dispatch(setChannel(null))
        dispatch(resetImport())
        dispatch(inactiveAllAsset())
      })
    }
  }
  const handleSelectChannel = useCallback(
    v => {
      batch(() => {
        dispatch(selectChannel(v))
        dispatch(setChannel(v))
        dispatch(resetImport())
        dispatch(resetActiveAssets())
      })
    },
    [dispatch]
  )

  useEffect(() => {
    if (selectedChannel && !readOnly) {
      ;(async () => {
        const {
          data: { generable },
        } = await api.creative.getCreativeIsAddable(selectedChannel.id)
        if (!generable) {
          showCreativeLimitAlert()
        }
      })()
    }
  }, [showCreativeLimitAlert, selectedChannel, readOnly])

  return (
    <>
      <div className="ad_managebox">
        <div className="managebox_tit">
          <strong className="tit_box">비즈채널 선택</strong>
        </div>
        <Table className="tbl_g3" caption="비즈채널 리스트 표">
          <colgroup>
            <col width={130} />
            <col />
          </colgroup>
          <Table.Body>
            <Table.Row className="tr_account">
              <th scope="row">
                <div className="inner_tbl">비즈채널 유형</div>
              </th>
              <td>
                <div className="inner_tbl">
                  <div className="item_filter">
                    <div className="filter_radio">
                      <RadioButton
                        checkValue={bizChannelType}
                        value={BizChannelTypes.WEBSITE_BIZ_CHANNEL}
                        label={
                          BizChannelTypeNames[
                            BizChannelTypes.WEBSITE_BIZ_CHANNEL
                          ]
                        }
                        onValueChange={handleChange}
                        disabled={readOnly || isChannelsLoading}
                      />
                      <RadioButton
                        checkValue={bizChannelType}
                        value={BizChannelTypes.TALK_CHANNEL}
                        label={
                          BizChannelTypeNames[BizChannelTypes.TALK_CHANNEL]
                        }
                        onValueChange={handleChange}
                        disabled={readOnly || isChannelsLoading}
                      />
                    </div>
                  </div>
                </div>
              </td>
            </Table.Row>
            <Table.Row>
              <th scope="row">
                <div className="inner_tbl">
                  총 비즈채널{' '}
                  <span className="fc_emph">{filteredChannels.length}</span>
                </div>
              </th>
              <td>
                <div className="inner_tbl">
                  {isChannelsLoading ? (
                    <Loading />
                  ) : filteredChannels && filteredChannels.length > 0 ? (
                    <SelectBox
                      placeholder={
                        selectedChannel?.name || '비즈채널을 선택하세요'
                      }
                      value={selectedChannel}
                      onValueChange={handleSelectChannel}
                      disabled={readOnly || isChannelsLoading}
                      footer={
                        <Link
                          to={`/${params.accountId}/channels/add?from=${from}`}
                          className="link_register">
                          비즈채널 신규 등록하기
                          <span className="img_comm ico_arr" />
                        </Link>
                      }>
                      {filteredChannels.map(channel => {
                        const { pcUrl, mobileUrl } = channel.siteUrl
                        return (
                          <SelectBox.Item
                            key={channel.id}
                            value={channel}
                            text={channel.name}>
                            <>
                              <span className="limit_line">{channel.name}</span>
                              {pcUrl && (
                                <span className="txt_addr">{`${pcUrl.protocol.toLowerCase()}://${
                                  pcUrl.url
                                }`}</span>
                              )}
                              {mobileUrl && (
                                <span className="txt_addr">{`${mobileUrl.protocol.toLowerCase()}://${
                                  mobileUrl.url
                                }`}</span>
                              )}
                            </>
                          </SelectBox.Item>
                        )
                      })}
                    </SelectBox>
                  ) : channelsError ? (
                    <p className="box_error">
                      <span className="ico_comm" />
                      일시적인 문제로 비즈채널을 불러올 수 없습니다.
                      <br />
                      페이지를 새로고침 하세요.
                    </p>
                  ) : (
                    <>
                      <p className="box_error">
                        <span className="ico_comm" />
                        비즈채널이 없습니다.
                      </p>
                      <p className="desc_item">
                        광고자산 관리 &gt; 비즈채널 관리에서{' '}
                        <Link
                          to={`/${params.accountId}/channels/add?from=${from}`}
                          className="fc_emph">
                          신규 비즈채널을 등록
                        </Link>
                        할 수 있습니다.
                      </p>
                    </>
                  )}
                  <CreativeLimitAlert>
                    <strong className="tit_layer">
                      선택한 비즈채널 내 등록 가능한
                      <br />
                      소재 개수가 초과되었습니다.
                    </strong>
                    비즈채널에 등록할 수 있는 소재 개수는 최대{' '}
                    {maxCount === 10000 ? '1만' : maxCount}개 입니다.
                    <br />
                    해당 비즈채널의 소재를 삭제하신 후에
                    <br />
                    등록 가능합니다.
                  </CreativeLimitAlert>
                </div>
              </td>
            </Table.Row>
          </Table.Body>
        </Table>
      </div>
      <Confirm title="비즈채널 유형 변경" onClose={handleClose}>
        유형 변경 시 기존 내용은 저장되지 않습니다.
      </Confirm>
    </>
  )
}

export default SelectChannel

SelectChannel.propTypes = {
  readOnly: PropTypes.bool,
}
